/**
 * The story's cards in order: you, then the chat, then friends, then the share
 * card. A card with nothing to show is left out rather than shown empty.
 */

import type { ReactElement } from "react";

import { ChatCalls } from "../cards/chat/ChatCalls";
import { ChatClock } from "../cards/chat/ChatClock";
import { ChatGif } from "../cards/chat/ChatGif";
import { ChatNames } from "../cards/chat/ChatNames";
import { ChatNumbers } from "../cards/chat/ChatNumbers";
import { Duo } from "../cards/chat/Duo";
import { Polls } from "../cards/chat/Polls";
import { Joins } from "../cards/chat/Joins";
import { WordOfMonth } from "../cards/chat/WordOfMonth";
import { Ceremony } from "../cards/friends/Ceremony";
import { Personalities } from "../cards/friends/Personalities";
import { Quiz } from "../cards/friends/Quiz";
import { Awards } from "../cards/you/Awards";
import { ShareCard } from "../share/ShareCard";
import { BiggestDay } from "../cards/you/BiggestDay";
import { Calls } from "../cards/you/Calls";
import { Clock } from "../cards/you/Clock";
import { Emoji } from "../cards/you/Emoji";
import { Intro } from "../cards/you/Intro";
import { Messages } from "../cards/you/Messages";
import { NotAround } from "../cards/you/NotAround";
import { Personality } from "../cards/you/Personality";
import { Sites } from "../cards/you/Sites";
import { Streak } from "../cards/you/Streak";
import { ThenVsNow } from "../cards/you/ThenVsNow";
import { TopPeople } from "../cards/you/TopPeople";
import { Words } from "../cards/you/Words";
import { YourGif } from "../cards/you/YourGif";
import { wr, type CardLook } from "../theme";
import type { Act, WrappedResponse } from "../types";

export type CardContext = { next: () => void };

export type CardSpec = {
  id: string;
  act: Act;
  /** How long it plays before moving on; ignored when it waits for a tap. */
  seconds: number;
  waitsForTap?: boolean;
  /** Overrides the card's colour block from `CARD_LOOKS`. */
  look?: CardLook;
  render: (ctx: CardContext) => ReactElement;
};

/** The clock card's sky: day, dusk or night at your peak hour. */
export function skyAt(hour: number): CardLook {
  const from: [number, number] = [0.5, 0];
  if (hour >= 6 && hour < 17) return { bg: wr("sky-day"), fg: wr("ink"), from };
  if (hour >= 17 && hour < 19) return { bg: wr("sky-dusk"), fg: wr("white"), from };
  return { bg: wr("sky-night"), fg: wr("white"), from };
}

export function buildDeck(data: WrappedResponse): CardSpec[] {
  const deck: CardSpec[] = [];
  const add = (id: string, act: Act, seconds: number, render: CardSpec["render"], waitsForTap = false, look?: CardLook) => {
    deck.push({ id, act, seconds, render, waitsForTap, look });
  };
  // Cards that wait for a tap play no timer, so their seconds are never read.
  const tap = (id: string, act: Act, render: CardSpec["render"]) => add(id, act, 0, render, true);

  const { me, chat, period } = data;
  const myName = me?.person.name ?? null;
  if (me) {
    const you = me.you;
    const { biggest_day: biggest, streak, calls } = you;
    const name = me.person.name;
    add("intro", "you", 4.5, () => <Intro name={name} period={period} />);
    add("messages", "you", 6.5, () => <Messages messages={you.messages} total={chat.numbers.messages} />);
    if (you.top_people.length) add("top-people", "you", 6.5, () => <TopPeople people={you.top_people} name={name} />);
    add("words", "you", 6.5, () => <Words words={you.words} />);
    if (you.emoji.length) add("emoji", "you", 7, () => <Emoji emoji={you.emoji} />);
    if (you.sites.length) add("sites", "you", 5.5, () => <Sites sites={you.sites} />);
    const gif = you.gif;
    if (gif) add("gif", "you", 5.5, () => <YourGif gif={gif} />);
    add("clock", "you", 6.5, () => <Clock clock={you.clock} />, false, skyAt(you.clock.peak_hour));
    if (biggest) add("biggest-day", "you", 7, () => <BiggestDay day={biggest} name={name} />);
    if (streak) add("streak", "you", 6.5, () => <Streak streak={streak} period={period} />);
    add("then-vs-now", "you", 6, () => <ThenVsNow then={you.then_vs_now} total={you.messages.total} name={name} period={period} />);
    if (calls) add("calls", "you", 6, () => <Calls calls={calls} />);
    add("awards", "you", 7, () => <Awards awards={you.awards} />);
    add("personality", "you", 8, () => <Personality personality={you.personality} />);
  } else {
    add("not-around", "you", 5, () => <NotAround period={period} />);
  }

  const { calls: chatCalls, duo, heat, gif: chatGif, polls = [] } = chat;
  add("chat-numbers", "chat", 6, () => <ChatNumbers numbers={chat.numbers} />);
  if (duo) add("duo", "chat", 6.5, () => <Duo duo={duo} />);
  if (chat.names.length) add("names", "chat", 7, () => <ChatNames names={chat.names} />);
  if (chatCalls) add("chat-calls", "chat", 6, () => <ChatCalls calls={chatCalls} />);
  if (heat?.some((hours) => hours.some(Boolean))) add("chat-clock", "chat", 6, () => <ChatClock heat={heat} />);
  add("word-of-month", "chat", 6.5, () => <WordOfMonth months={chat.word_of_month} />);
  if (chatGif) add("chat-gif", "chat", 5.5, () => <ChatGif gif={chatGif} />);
  if (chat.joins.length) add("joins", "chat", 5.5, () => <Joins joins={chat.joins} />);
  if (polls.length) add("polls", "chat", 6.5, () => <Polls polls={polls} />);

  chat.quiz.forEach((question, i) =>
    tap(`quiz-${i + 1}`, "friends", ({ next }) => (
      <Quiz {...question} onDone={next} index={i + 1} of={chat.quiz.length} me={myName} />
    )),
  );
  if (chat.ceremony.length) {
    tap("ceremony", "friends", () => <Ceremony ceremony={chat.ceremony} me={myName} />);
    add("personalities", "friends", 6.5, () => <Personalities ceremony={chat.ceremony} me={myName} />);
  }

  if (me) tap("share", "share", () => <ShareCard story={me} period={period} />);
  return deck;
}
